/**
 * 日志系统 - 基于 pino，统一携带 traceId
 */

import pino from 'pino';
import { v4 as uuidv4 } from 'uuid';
import type { LogLevel, LogEntry } from '../types';

// 日志配置
interface LoggerConfig {
  level?: LogLevel;
  pretty?: boolean;
  traceId?: string;
}

// 默认配置
const DEFAULT_CONFIG: Required<LoggerConfig> = {
  level: 'info',
  pretty: typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true,
  traceId: '',
};

/**
 * Logger 类 - 统一日志管理
 */
export class Logger {
  private pino: pino.Logger;
  private traceId: string;

  constructor(config: LoggerConfig = {}) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    
    this.traceId = finalConfig.traceId || uuidv4();
    
    this.pino = pino({
      level: finalConfig.level,
      transport: finalConfig.pretty ? {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'SYS:standard',
          ignore: 'pid,hostname',
        },
      } : undefined,
      base: {
        traceId: this.traceId,
      },
    });
  }

  /**
   * 获取当前 traceId
   */
  getTraceId(): string {
    return this.traceId;
  }

  /**
   * 设置新的 traceId
   */
  setTraceId(traceId: string): void {
    this.traceId = traceId;
    // 创建新的 logger 实例以更新 base 字段
    this.pino = this.pino.child({ traceId });
  }

  /**
   * 创建子 logger，继承当前配置但使用新的 traceId
   */
  child(traceId?: string): Logger {
    const newTraceId = traceId || uuidv4();
    return new Logger({
      level: this.pino.level as LogLevel,
      pretty: typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true,
      traceId: newTraceId,
    });
  }

  /**
   * Debug 日志
   */
  debug(message: string, context?: Record<string, any>): void {
    this.pino.debug({ ...context, traceId: this.traceId }, message);
  }

  /**
   * Info 日志
   */
  info(message: string, context?: Record<string, any>): void {
    this.pino.info({ ...context, traceId: this.traceId }, message);
  }

  /**
   * Warning 日志
   */
  warn(message: string, context?: Record<string, any>): void {
    this.pino.warn({ ...context, traceId: this.traceId }, message);
  }

  /**
   * Error 日志
   */
  error(message: string, context?: Record<string, any>): void {
    this.pino.error({ ...context, traceId: this.traceId }, message);
  }

  /**
   * 记录规则验证开始
   */
  ruleStart(ruleName: string, nodeId: string, nodeName: string): void {
    this.info(`规则验证开始: ${ruleName}`, {
      ruleName,
      nodeId,
      nodeName,
      phase: 'rule_start',
    });
  }

  /**
   * 记录规则验证结束
   */
  ruleEnd(ruleName: string, nodeId: string, valid: boolean, duration: number): void {
    this.info(`规则验证结束: ${ruleName}`, {
      ruleName,
      nodeId,
      valid,
      duration,
      phase: 'rule_end',
    });
  }

  /**
   * 记录 Figma API 调用
   */
  figmaApiCall(method: string, url: string, fileKey?: string): void {
    this.debug(`Figma API 调用: ${method} ${url}`, {
      method,
      url,
      fileKey,
      phase: 'figma_api',
    });
  }

  /**
   * 记录 Figma API 响应
   */
  figmaApiResponse(method: string, url: string, status: number, duration: number): void {
    this.debug(`Figma API 响应: ${method} ${url}`, {
      method,
      url,
      status,
      duration,
      phase: 'figma_api_response',
    });
  }

  /**
   * 记录规则引擎启动
   */
  engineStart(nodeCount: number, ruleCount: number): void {
    this.info('规则引擎启动', {
      nodeCount,
      ruleCount,
      phase: 'engine_start',
    });
  }

  /**
   * 记录规则引擎完成
   */
  engineEnd(nodeCount: number, validationCount: number, duration: number): void {
    this.info('规则引擎完成', {
      nodeCount,
      validationCount,
      duration,
      phase: 'engine_end',
    });
  }

  /**
   * 生成日志条目对象
   */
  createLogEntry(level: LogLevel, message: string, context?: Record<string, any>): LogEntry {
    return {
      traceId: this.traceId,
      level,
      message,
      timestamp: new Date(),
      context,
    };
  }
}

// 默认 logger 实例
export const logger = new Logger();

// 创建带有指定 traceId 的 logger
export function createLogger(traceId?: string): Logger {
  return new Logger({ traceId });
}

// 便捷方法
export function withTraceId(traceId: string) {
  return createLogger(traceId);
}